import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/glTransaction.mutation"

import { type GlTransactionReverseInput, ReverseGlTransactionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseReverseGlTransactionReturn {
  reverseGlTransaction: (input: GlTransactionReverseInput) => Promise<boolean>
  isReversing: boolean
}

export const useReverseGlTransaction = (): UseReverseGlTransactionReturn => {
  const [{ fetching }, executeMutation] = useMutation(ReverseGlTransactionDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const reverseGlTransaction = useCallback(
    async (input: GlTransactionReverseInput): Promise<boolean> => {
      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: result.error.message || "Failed to reverse transaction. Please try again.",
        })
        return false
      }

      const output = result.data?.glTransaction.reverse

      if (output?.__typename === "GlTransactionFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: output.error.message || "Failed to reverse transaction. Please try again.",
        })
        return false
      }

      const referenceNumber =
        output?.__typename === "GlTransactionReverseSuccess" ? output.glTransaction.referenceNumber : undefined

      notify({
        type: NotificationType.Success,
        silent: false,
        message: `GL Entry reversed.${referenceNumber ? ` Reference: ${referenceNumber}` : ""}`,
      })
      return true
    },
    [executeMutation, notify]
  )

  return { reverseGlTransaction, isReversing: fetching }
}
